import { fetchWithTimeout } from './requestTimeout.js';
import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { Capacitor } from '@capacitor/core';
import { getActiveOperatorRequestHeaders } from './auth/activeOperatorSession.js';
import { AUTH_STORAGE_KEY, createAuthStorageGeneration, retireAuthStorage } from './auth/sessionStorage.js';

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  // Fail fast so misconfigured builds don't silently run unauthenticated.
  throw new Error('VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set');
}

let client: SupabaseClient | null = null;

const operatorAwareFetch: typeof fetch = (input, init) => {
  const requestUrl = new URL(
    typeof input === 'string' || input instanceof URL ? input.toString() : input.url,
  );
  const headers = new Headers(input instanceof Request ? input.headers : undefined);
  if (init?.headers) {
    new Headers(init.headers).forEach((value, key) => headers.set(key, value));
  }
  if (requestUrl.origin === new URL(url).origin) {
    for (const [key, value] of Object.entries(getActiveOperatorRequestHeaders())) {
      headers.set(key, value);
    }
  }
  const bounded = requestUrl.origin === new URL(url).origin
    && /^\/(auth|rest)\/v1\//.test(requestUrl.pathname);
  return bounded ? fetchWithTimeout(input, { ...init, headers }) : fetch(input, { ...init, headers });
};

export function getSupabase(): SupabaseClient {
  if (client) return client;
  // PKCE è attivo su entrambe le piattaforme (login Google). La differenza è
  // come torna il `?code=`:
  // - WEB/PWA: redirect full-page → `/today?code=…`. Non c'è alcun deep link
  //   da intercettare (il DeepLinkHandler ascolta `appUrlOpen`, evento solo
  //   nativo), quindi serve `detectSessionInUrl: true` perché supabase-js
  //   scambi automaticamente il code per la sessione al caricamento pagina.
  // - NATIVO: Custom Tab → deep link `trackeat://auth-callback?code=…`, che il
  //   DeepLinkHandler scambia a mano con exchangeCodeForSession; qui
  //   `detectSessionInUrl` resta `false` per non innescare un doppio exchange.
  // Non impatta signInWithPassword/signUp.
  const isNative = Capacitor.isNativePlatform();
  const instance = createAuthStorageGeneration(operatorAwareFetch);
  client = createClient(url, anonKey, {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: !isNative,
      flowType: 'pkce',
      storageKey: AUTH_STORAGE_KEY,
      storage: instance.storage,
    },
    global: {
      fetch: instance.fetch,
      headers: {
        'x-client-info': 'niveo-mobile/0.1',
      },
    },
  });
  return client;
}

/** Retire before any await: old auth work cannot repopulate a later account. */
export function resetSupabaseAuth(clearSession = true): void {
  const previous = client;
  retireAuthStorage(clearSession);
  client = null;
  void previous?.auth.stopAutoRefresh().catch(() => undefined);
}

export const SUPABASE_URL = url;
export const SUPABASE_ANON_KEY = anonKey;

// Base URL per la pagina pubblica di tracciabilità (QR su etichette).
// Punta al proxy Cloudflare Pages che riemette l'HTML con Content-Type corretto
// (Supabase forza text/plain + CSP sandbox su *.supabase.co, anti-phishing).
export const TRACE_BASE_URL: string =
  (import.meta.env.VITE_TRACE_BASE_URL as string | undefined) || 'https://trackeat.eu';
